import React from 'react'
import { Box, Paper, Stack, Table, TableBody, TableContainer, TableFooter, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { grey, blue } from '@mui/material/colors'
import { useGlobalState } from 'state'
import { ObservatoryValue, ObservatoryRow } from 'common/types'
import { StyledTableCell } from 'components/ui/StyledTableCell'
import { StyledTableRow } from 'components/ui/StyledTableRow'
import { RainChart } from './RainChart'
import dayjs from 'dayjs'


export const RainDetail = () => {
  const state = useGlobalState()
  const [rain, setRain] = React.useState<ObservatoryRow | null>(null)
  const [timeSeries, setTimeSeries] = React.useState<ObservatoryValue[]>([])
  const [forecastHours, setForecastHours] = React.useState<number>(6)
  const [timeInterval, setTimeInterval] = React.useState<number>(10)

  React.useEffect(() => {
    setRain(state.observatory.selected)
  }, [state.observatory.selected])

  React.useEffect(() => {
    let arr = state.observatory.timeSeries.map(ts => {
      const jst = dayjs.utc(ts.date).local().format('YYYY/MM/DD HH:mm')
      return {...ts, date: jst}
    })
    if (timeInterval === 60) {
      arr = arr.filter(ts => ts.date.slice(-2) === '00' && (ts.min60 !== undefined || ts.total !== undefined))
    } else {
      arr = arr.filter(ts => (ts.min10 !== undefined || ts.total !== undefined))
    }
    const minDate = state.app.baseDate.subtract(timeInterval === 60 ? 23 : 3, 'hours').local().format('YYYY/MM/DD HH:mm')
    const maxDate = state.app.baseDate.add(forecastHours, 'hours').local().format('YYYY/MM/DD HH:mm')
    arr = arr.filter(ts => ts.date >= minDate && ts.date <= maxDate)

    setTimeSeries(arr)
  }, [state.observatory.timeSeries, forecastHours, timeInterval])

  const forecastHoursChange = (
    event: React.MouseEvent<HTMLElement>,
    value: number,
  ) => {
    setForecastHours(value)
  }

  const timeIntervalChange = (
    event: React.MouseEvent<HTMLElement>,
    value: number,
  ) => {
    setTimeInterval(value)
  }

  const getTimeSeriesColor = (row: ObservatoryValue) => {
    return row.type === 'forecast' ? blue[500] : grey[900]
  }

  return ( rain &&
    <Box>

      <Stack direction="row" width={230}>
        <Typography variant='caption' color={grey[700]} flexGrow={1}>予測期間</Typography>
        <Typography variant='caption' color={grey[700]}>時間表示</Typography>
      </Stack>
      <Stack direction="row" mb={1} spacing={2}>
        <ToggleButtonGroup size="small" exclusive value={forecastHours} onChange={forecastHoursChange}>
          <ToggleButton sx={{height: 30}} value={6}>6時間</ToggleButton>
          <ToggleButton sx={{height: 30}} value={36}>36時間</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" exclusive value={timeInterval} onChange={timeIntervalChange}>
          <ToggleButton sx={{height: 30}} value={10}>10分毎</ToggleButton>
          <ToggleButton sx={{height: 30}} value={60}>時間毎</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      { rain && timeSeries.length > 0 && (
        <Stack direction="column">
          <Stack direction="row" alignItems={'center'}>
            <Typography variant='caption' flexGrow={1}>（mm/{timeInterval}min）</Typography>
            <Typography variant='caption'>（mm）</Typography>
          </Stack>
          <RainChart rain={rain} timeInterval={timeInterval} height={220} timeSeries={timeSeries} />
        </Stack>
      )}

      <TableContainer component={Paper}>
        <Table size="small" aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell width={160}>日時</StyledTableCell>
              <StyledTableCell>{timeInterval === 60 ? '時間' : '10分'} (㎜)</StyledTableCell>
              <StyledTableCell>累加 (㎜)</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {timeSeries.map((ts, i) => (
              <StyledTableRow key={i}>
                <StyledTableCell>
                  <Typography variant='body2' color={getTimeSeriesColor(ts)}>{ts.date}</Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant='body2' textAlign={'right'} color={getTimeSeriesColor(ts)}>
                    { timeInterval === 10 && (ts.min10 === undefined ? '-' : ts.min10.toFixed(2)) }
                    { timeInterval === 60 && (ts.min60 === undefined ? '-' : ts.min60.toFixed(2)) }
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant='body2' textAlign={'right'} color={getTimeSeriesColor(ts)}>
                    {ts.total === undefined ? '-' : ts.total.toFixed(2)}
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
          {
            timeSeries.length === 0 && (
              <TableFooter>
                <TableRow>
                  <StyledTableCell colSpan={3}>データがありません</StyledTableCell>
                </TableRow>
              </TableFooter>
            )
          }
        </Table>
      </TableContainer>

    </Box>
  )
}
